import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const InventoryCard = ({ item, onEdit, onDelete, onViewImages, isSelected, onSelect, onPrintQR }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'perfect':
        return 'bg-success/10 text-success border-success/20';
      case 'damaged':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getDeliveryStatusColor = (deliveryStatus) => {
    switch (deliveryStatus?.toLowerCase()) {
      case 'in_warehouse':
        return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'in_route':
        return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
      case 'delivered':
        return 'bg-success/10 text-success border-success/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getDeliveryStatusLabel = (deliveryStatus) => {
    switch (deliveryStatus?.toLowerCase()) {
      case 'in_warehouse':
        return 'In Warehouse';
      case 'in_route':
        return 'In Route';
      case 'delivered':
        return 'Delivered';
      default:
        return deliveryStatus;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  return (
    <div className={`bg-card rounded-lg border transition-smooth overflow-hidden ${
      isSelected ? 'border-primary elevation-2' : 'border-border elevation-1 hover:elevation-2'
    }`}>
      <div className="relative">
        <div className="absolute top-3 left-3 z-10">
          <button
            onClick={() => onSelect(item?.id)}
            className={`w-6 h-6 rounded border-2 transition-smooth flex items-center justify-center ${
              isSelected 
                ? 'bg-primary border-primary' :'bg-background border-border hover:border-primary'
            }`}
            aria-label={isSelected ? 'Deselect item' : 'Select item'}
          >
            {isSelected && <Icon name="Check" size={16} color="white" />}
          </button>
        </div>

        <div className="absolute top-3 right-3 z-10">
          <span className={`text-xs px-3 py-1 rounded-full border font-medium ${getStatusColor(item?.status)}`}>
            {item?.status}
          </span>
        </div>

        <div className="absolute top-12 right-3 z-10">
          <span className={`text-xs px-3 py-1 rounded-full border font-medium ${getDeliveryStatusColor(item?.deliveryStatus)}`}>
            {getDeliveryStatusLabel(item?.deliveryStatus)}
          </span>
        </div>

        <div className="w-full h-64 overflow-hidden bg-muted">
          <Image
            src={item?.primaryImage}
            alt={item?.primaryImageAlt}
            className="w-full h-full object-cover"
          />
        </div>

        {item?.imageCount > 1 && (
          <button
            onClick={() => onViewImages(item)}
            className="absolute bottom-3 right-3 bg-background/90 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-border hover:bg-background transition-smooth touch-target"
          >
            <div className="flex items-center gap-2">
              <Icon name="Images" size={16} color="var(--color-primary)" />
              <span className="text-xs font-medium text-foreground">{item?.imageCount} photos</span>
            </div>
          </button>
        )}
      </div>
      <div className="p-4">
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-foreground mb-1">{item?.name}</h3>
          <p className="text-sm text-muted-foreground font-mono">{item?.liin}</p>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
            <span className="text-sm text-muted-foreground">Arrived: {formatDate(item?.arrivalDate)}</span>
          </div>

          {item?.notes && (
            <div className="flex items-start gap-2">
              <Icon name="FileText" size={16} color="var(--color-muted-foreground)" className="mt-0.5" />
              <p className="text-sm text-muted-foreground line-clamp-2">{item?.notes}</p>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPrintQR?.(item)}
            iconName="QrCode"
            iconPosition="left"
            iconSize={16}
            className="flex-1"
          >
            QR Label
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(item)}
            iconName="Edit"
            iconPosition="left"
            iconSize={16}
            className="flex-1"
          >
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(item)}
            iconName="Trash2"
            iconPosition="left"
            iconSize={16}
            className="flex-1 text-error hover:text-error hover:border-error"
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InventoryCard;